import { useState, useEffect, useCallback } from 'react';
import { Ban, Unlock, RefreshCcw, Loader2, ShieldCheck, MapPin, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const REASON_LABELS = {
  role_escalation_attempt: 'Role escalation attempt',
  order_price_tamper_attempt: 'Order price tampering',
  paid_order_modification_attempt: 'Paid order modification',
};

export function BlockedUsersTab({ token }) {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState('');

  const fetchBlocked = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/security/blocked-users`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) {
        const d = await r.json();
        setBlocked(d.blocked_users || []);
      }
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchBlocked(); }, [fetchBlocked]);

  const unblock = async (userId) => {
    setUnblocking(userId);
    try {
      const r = await fetch(`${API_URL}/api/admin/security/unblock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed to unblock');
      toast.success(d.message || 'User unblocked');
      setBlocked(prev => prev.filter(u => u.user_id !== userId));
    } catch (e) { toast.error(e.message); }
    finally { setUnblocking(''); }
  };

  return (
    <Card data-testid="blocked-users-tab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              Firewall — Blocked Users
            </CardTitle>
            <CardDescription>
              Accounts permanently auto-blocked for attempting to breach the platform. Only you can restore access.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchBlocked} className="gap-2" data-testid="blocked-users-refresh">
            <RefreshCcw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : blocked.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-muted-foreground" data-testid="blocked-users-empty">
            <ShieldCheck className="h-8 w-8 mb-2 text-emerald-500" />
            <p className="text-sm">No blocked users — the firewall hasn't caught anyone.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {blocked.map(u => {
              const loc = u.last_alert?.location || {};
              const locStr = [loc.city, loc.region, loc.country].filter(Boolean).join(', ');
              return (
                <div key={u.user_id} className="border rounded-lg p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`blocked-user-${u.user_id}`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold">{u.name || 'Unknown'}</p>
                      <Badge variant="destructive">{REASON_LABELS[u.blocked_reason] || u.blocked_reason || 'breach attempt'}</Badge>
                      <Badge variant="outline">{u.role}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
                    <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-muted-foreground">
                      {u.last_alert?.ip_address && (
                        <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> {u.last_alert.ip_address}</span>
                      )}
                      {locStr && (
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {locStr}</span>
                      )}
                      <span>Blocked {u.blocked_at ? new Date(u.blocked_at).toLocaleString() : ''} by {u.blocked_by === 'system_firewall' ? 'Firewall (auto)' : u.blocked_by}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => unblock(u.user_id)}
                    disabled={unblocking === u.user_id}
                    className="gap-2"
                    data-testid={`unblock-btn-${u.user_id}`}
                  >
                    {unblocking === u.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />}
                    Unblock
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default BlockedUsersTab;
